import { Plus, Trash2, Layers, Scale } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

/**
 * ItemBreakdownEditor — capture a mix of item types per parcel entry.
 * Each row: { type, count, weight_kg?, dimensions?, description? }
 *
 * Auto-computes:
 *   totalCount   = Σ count
 *   totalWeight  = Σ (weight_kg * count)   [only sums rows that entered a weight]
 *
 * Emits `value` array to onChange. Parent typically syncs `item_count` = totalCount
 * and `weight_kg` = totalWeight (when > 0) so backend + PDFs stay consistent.
 */

export const ITEM_TYPES = [
  "Document",
  "Envelope",
  "Parcel",
  "Package",
  "Box",
  "Fragile Item",
  "Other",
];

export default function ItemBreakdownEditor({ value, onChange, prefix = "items" }) {
  const rows = Array.isArray(value) ? value : [];
  const totalCount = rows.reduce((n, r) => n + (Number(r?.count) || 0), 0);
  const totalWeight = rows.reduce((n, r) => n + ((Number(r?.weight_kg) || 0) * (Number(r?.count) || 0)), 0);

  const addRow = () => onChange([...(rows || []), { type: "Parcel", count: 1, weight_kg: null, dimensions: "", description: "" }]);
  const setRow = (i, patch) => onChange(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="border border-border rounded-md p-3 bg-muted/20 space-y-3" data-testid={`${prefix}-editor`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-primary" />
          <div className="overline">Item breakdown <span className="normal-case tracking-normal font-normal text-muted-foreground">· mix types, weights & sizes</span></div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Total items</span>
            <span className="font-mono font-bold text-primary text-lg leading-none" data-testid={`${prefix}-total`}>{totalCount}</span>
          </div>
          {totalWeight > 0 && (
            <div className="flex items-center gap-1.5" data-testid={`${prefix}-total-weight-wrap`}>
              <Scale size={11} className="text-muted-foreground" />
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Total weight</span>
              <span className="font-mono font-bold text-primary text-lg leading-none" data-testid={`${prefix}-total-weight`}>
                {totalWeight.toFixed(2)} kg
              </span>
            </div>
          )}
        </div>
      </div>

      {rows.length === 0 && (
        <div className="text-xs text-muted-foreground">
          One parcel with a single item type + weight? Just use the parent fields above.
          For mixed items with different weights or sizes (e.g. 2 documents (0.2 kg each) + 3 parcels (1.5 kg each) + 1 fragile 20×20×15 cm), tap <b>"+ Add item type"</b>.
        </div>
      )}

      {rows.length > 0 && (
        <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground px-1 pb-1 border-b border-border/60">
          <div className="col-span-3">Type</div>
          <div className="col-span-1 text-center">Count</div>
          <div className="col-span-2">Weight (kg / unit)</div>
          <div className="col-span-2">Dimensions</div>
          <div className="col-span-3">Note</div>
          <div className="col-span-1" />
        </div>
      )}

      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`${prefix}-row-${i}`}>
          <div className="col-span-3">
            <Select value={r.type || "Parcel"} onValueChange={(v) => setRow(i, { type: v })}>
              <SelectTrigger data-testid={`${prefix}-type-${i}`} className="rounded-md border-input h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-1">
            <Input
              data-testid={`${prefix}-count-${i}`}
              type="number" min="1" step="1"
              value={r.count ?? 1}
              onChange={(e) => setRow(i, { count: Math.max(1, parseInt(e.target.value) || 1) })}
              className="rounded-md border-input font-mono text-center h-9 px-1"
            />
          </div>
          <div className="col-span-2">
            <Input
              data-testid={`${prefix}-weight-${i}`}
              type="number" min="0" step="0.01"
              value={r.weight_kg ?? ""}
              onChange={(e) => setRow(i, { weight_kg: e.target.value === "" ? null : parseFloat(e.target.value) })}
              className="rounded-md border-input font-mono h-9"
              placeholder="e.g. 0.5"
            />
          </div>
          <div className="col-span-2">
            <Input
              data-testid={`${prefix}-dims-${i}`}
              value={r.dimensions || ""}
              onChange={(e) => setRow(i, { dimensions: e.target.value })}
              className="rounded-md border-input h-9"
              placeholder="LxWxH cm"
            />
          </div>
          <div className="col-span-3">
            <Input
              data-testid={`${prefix}-desc-${i}`}
              value={r.description || ""}
              onChange={(e) => setRow(i, { description: e.target.value })}
              className="rounded-md border-input h-9"
              placeholder="Note (optional)"
            />
          </div>
          <div className="col-span-1 flex justify-end">
            <button
              type="button"
              data-testid={`${prefix}-remove-${i}`}
              onClick={() => removeRow(i)}
              className="text-muted-foreground hover:text-primary p-2 rounded-md hover:bg-primary/10 transition-colors"
              title="Remove row"
            ><Trash2 size={14} /></button>
          </div>
        </div>
      ))}

      <button
        type="button"
        data-testid={`${prefix}-add`}
        onClick={addRow}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary border border-dashed border-primary/50 hover:bg-primary hover:text-white rounded-md px-3 py-1.5 transition-colors"
      >
        <Plus size={12} /> Add item type
      </button>
    </div>
  );
}
